import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useTransaction } from '../../../../context/TransactionContext';
import { Transaction, TransactionIssueType, TransactionTypeValue } from '../../../../services/transaction.types';
import BuySection from './components/BuySection';
import CommentSection from './components/CommentSection';
import FeeSection from './components/FeeSection';
import SellSection from './components/SellSection';
import TransactionHeader from './components/TransactionHeader';

import { useTransactionCalculations } from './hooks/useTransactionCalculations';
import TransactionTypeModal from './TransactionTypeModal';

interface TransactionFormProps {
    transaction: Transaction | null;
    onSubmit: (values: Partial<Transaction>) => void;
    onSaveDisabledChange: (state: boolean) => void;
}

const TransactionForm = React.forwardRef<HTMLFormElement, TransactionFormProps>(
    ({ transaction, onSubmit, onSaveDisabledChange }, ref) => {
        //We use only filtered transaction types without transfer
        const { transactionTypes, accounts } = useTransaction();
        const { currentCtUser } = useAuth();
        const [typeModalOpen, setTypeModalOpen] = useState(!transaction);

        const [formValues, setFormValues] = useState<Partial<Transaction>>({
            id: transaction?.id || undefined,
            typeId: transaction?.typeId || 0,
            date: transaction?.date || Math.floor(Date.now() / 1000),
            account: transaction?.account || '',
            accountIncoming: transaction?.accountIncoming || '',
            buy: transaction?.buy || 0,
            buyCoin: transaction?.buyCoin || '',
            buyPricePerUnit:
                transaction?.buyPricePerUnit || transaction?.buyValueInFiat
                    ? transaction?.buyValueInFiat / transaction?.buy
                    : 0 || 0,
            buyValueInFiat: transaction?.buyValueInFiat || 0,
            sell: transaction?.sell || 0,
            sellCoin: transaction?.sellCoin || '',
            sellPricePerUnit:
                transaction?.sellPricePerUnit || transaction?.sellValueInFiat
                    ? transaction?.sellValueInFiat / transaction?.sell
                    : 0 || 0,
            sellValueInFiat: transaction?.sellValueInFiat || 0,
            feePricePerUnit: transaction?.feePricePerUnit || 0,
            feeValueInFiat: transaction?.feeValueInFiat || 0,
            fee: transaction?.fee || 0,
            feeCoin: transaction?.feeCoin || '',
            comment: transaction?.comment || '',
            buyPoolName: transaction?.buyPoolName || '',
            sellPoolName: transaction?.sellPoolName || '',
            buyValueInBtc: transaction?.buyValueInBtc || 0,
            sellValueInBtc: transaction?.sellValueInBtc || 0,
            buyTicker: transaction?.buyTicker || '',
            sellTicker: transaction?.sellTicker || '',
            feeTicker: transaction?.feeTicker || '',
        });

        const handleChange = (field: keyof Transaction, value: any) => {
            setFormValues((prev) => {
                const newValues = {
                    ...prev,
                    [field]: value,
                };
                return newValues;
            });
        };

        const handleSubmit = (event: React.FormEvent) => {
            event.preventDefault();
            onSubmit(formValues);
        };

        const isIncomingOnly = useMemo(() => {
            const type = transactionTypes.find((t) => t.value === formValues.typeId);
            return type?.in && !type?.out;
        }, [formValues.typeId]);

        const selectedType = transactionTypes.find((type) => type.value === formValues.typeId);
        const isTransfer = false; // Transfer type is removed
        const showBuyFields = selectedType?.in;
        const showSellFields = selectedType?.out;
        const showLiquidityPool =
            selectedType?.value === TransactionTypeValue.OutProvideLiquidity ||
            selectedType?.value === TransactionTypeValue.InRemoveLiquidity ||
            selectedType?.value === TransactionTypeValue.OutReturnLpToken ||
            selectedType?.value === TransactionTypeValue.InReceiveLpToken;

        const hasMissingPrices = transaction?.issueType === TransactionIssueType.MissingPrice;

        const isSaveDisabled = useMemo(() => {
            const type = transactionTypes.find((t) => t.value === formValues.typeId);
            if (formValues.typeId === undefined) return true;
            if (type === undefined) return true;

            // Check if account is empty
            if (!formValues.account) return true;

            if (type.in && type.out) {
                const hasBuy = formValues.buy && formValues.buyCoin;
                const hasSell = formValues.sell && formValues.sellCoin;

                return !(hasBuy && hasSell);
            }

            if (type.in && !type.out) {
                return !formValues.buy || !formValues.buyCoin;
            }
            if (!type.in && type.out) {
                return !formValues.sell || !formValues.sellCoin;
            }

            return false;
        }, [formValues]);

        useEffect(() => {
            onSaveDisabledChange(isSaveDisabled);
        }, [isSaveDisabled]);

        const {
            formatInput,
            handleBuyAmountChange,
            handleBuyPriceChange,
            handleBuyValueChange,
            handleBuyCoinChange,
            handleSellAmountChange,
            handleSellPriceChange,
            handleSellValueChange,
            handleSellCoinChange,
            handleDateChange,
            handleTimeChange,
            handleAccountChange,
            handleFeeCoinChange,
            handleFeeAmountChange,
            handleFeePriceChange,
            handleFeeValueChange,
            handleCommentChange,
            handleBuyPoolChange,
            handleSellPoolChange,
            isUpdatingPrices,
            liquidityPools,
            isLiquidityPoolLoading,
        } = useTransactionCalculations(formValues, handleChange, showLiquidityPool, transactionTypes);

        return (
            <Box component="form" id="transaction-form" ref={ref} onSubmit={handleSubmit} sx={{ p: 3 }}>
                <TransactionHeader
                    formValues={formValues}
                    accounts={accounts}
                    selectedType={selectedType}
                    onDateChange={handleDateChange}
                    onTimeChange={handleTimeChange}
                    onAccountChange={handleAccountChange}
                    onTypeModalOpen={() => setTypeModalOpen(true)}
                />
                <Box sx={{ mt: 3 }}>
                    {showBuyFields && (
                        <BuySection
                            formValues={formValues}
                            currentCtUser={currentCtUser}
                            onAmountChange={handleBuyAmountChange}
                            onPriceChange={handleBuyPriceChange}
                            onValueChange={handleBuyValueChange}
                            onCoinChange={handleBuyCoinChange}
                            onPoolChange={handleBuyPoolChange}
                            liquidityPools={liquidityPools}
                            hasMissingPrices={hasMissingPrices}
                            formatInput={formatInput}
                            showLiquidityPool={showLiquidityPool}
                            isUpdatingPrices={isUpdatingPrices.buy}
                            isTransfer={isTransfer}
                            isLiquidityPoolLoading={isLiquidityPoolLoading}
                        />
                    )}

                    {showSellFields && (
                        <SellSection
                            formValues={formValues}
                            currentCtUser={currentCtUser}
                            onAmountChange={handleSellAmountChange}
                            onPriceChange={handleSellPriceChange}
                            onValueChange={handleSellValueChange}
                            onCoinChange={handleSellCoinChange}
                            onPoolChange={handleSellPoolChange}
                            liquidityPools={liquidityPools}
                            hasMissingPrices={hasMissingPrices}
                            formatInput={formatInput}
                            showLiquidityPool={showLiquidityPool}
                            isUpdatingPrices={isUpdatingPrices.sell}
                            isLiquidityPoolLoading={isLiquidityPoolLoading}
                        />
                    )}

                    {!isIncomingOnly && (
                        <FeeSection
                            formValues={formValues}
                            currentCtUser={currentCtUser}
                            onAmountChange={handleFeeAmountChange}
                            onPriceChange={handleFeePriceChange}
                            onValueChange={handleFeeValueChange}
                            onCoinChange={handleFeeCoinChange}
                            formatInput={formatInput}
                            isUpdatingPrices={isUpdatingPrices.fee}
                            showBuyFields={showBuyFields}
                            showSellFields={showSellFields}
                        />
                    )}
                    <CommentSection formValues={formValues} onCommentChange={handleCommentChange} />
                </Box>
                <TransactionTypeModal
                    open={typeModalOpen}
                    onClose={() => setTypeModalOpen(false)}
                    onSelect={(type) => {
                        handleChange('typeId', type.value);
                        setTypeModalOpen(false);
                    }}
                    transactionTypes={transactionTypes}
                />
            </Box>
        );
    }
);

export default TransactionForm;
